import type {FsCategoryLine} from '@/store/audit/fsCategoryStore';
import {useFsAdjustStore} from '@/store/audit';
import type {CommonRow} from '@/services/auditService/interface/wp-row';
import {
    applyDrMergeLine,
    applyTaxPayableBfMergeLine,
    getOriginalEntryFromParent,
    isTaxBfSpecialDtdCode,
    isTaxPayableBfDtdCode,
    isTaxRefundableBfDtdCode,
} from '@/adjustment/adjust/utils/specialAdjustFlow';
import {
    getOriginalEntryFromFsCategory,
    mergeDataForAdjust,
    type Wp2WpResult,
} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {isAdjustmentLocked} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import {
    resolveTaxAdjustmentSign,
    TAX_CF_SOURCE_ROW_COUNT,
    taxLogicalRowIndex,
} from '@/pages/components/audit/adjustment/adjust/dialogs/tax/taxSelfAdjustment';

const TAX_REFUNDABLE_BF_ROW_INDEX = 0;
const TAX_PAYABLE_BF_ROW_INDEX = 1;
const TAX_PAID_ROW_INDEX = 2;
const TAX_REFUNDED_ROW_INDEX = 3;
const TAX_EXPENSES_ROW_INDEX = 4;

/** TaxWp 在 FS 树中对应的三个叶子节点 */
export const TAX_WP_FS_CODES = new Set(['BS-CA-TAXR', 'BS-CL-TAXP', 'PL-TAX']);

/**
 * 非 TaxWp 发起 adjustment、在 FsSelectionDrawer 选中 Tax 节点作为 toPaper 时，
 * 默认绑定的 TaxWp commonRow 逻辑行（单行自动绑定时的 fallback）：
 * - BS-CA-TAXR → Tax (refundable) b/f
 * - BS-CL-TAXP → Tax payable b/f
 * - PL-TAX → Tax expenses during the period
 */
const TAX_WP_TO_PAPER_DEFAULT_LOGICAL_INDEX: Record<string, number> = {
    'BS-CA-TAXR': TAX_REFUNDABLE_BF_ROW_INDEX,
    'BS-CL-TAXP': TAX_PAYABLE_BF_ROW_INDEX,
    'PL-TAX': TAX_EXPENSES_ROW_INDEX,
};

/**
 * 非 TaxWp → Tax 节点 toPaper：RowSelector 可选逻辑行。
 * - BS-CA-TAXR → refundable b/f + paid + refunded
 * - BS-CL-TAXP → payable b/f + paid + refunded
 * - PL-TAX → expenses only（保持单行自动绑定）
 */
const TAX_WP_TO_PAPER_SELECTABLE_LOGICAL_INDICES: Record<string, readonly number[]> = {
    'BS-CA-TAXR': [TAX_REFUNDABLE_BF_ROW_INDEX, TAX_PAID_ROW_INDEX, TAX_REFUNDED_ROW_INDEX],
    'BS-CL-TAXP': [TAX_PAYABLE_BF_ROW_INDEX, TAX_PAID_ROW_INDEX, TAX_REFUNDED_ROW_INDEX],
    'PL-TAX': [TAX_EXPENSES_ROW_INDEX],
};

/**
 * Non–Self Adjustment: logical row index → fromPaper FS code (null = needs user pick).
 * Tax (paid) / Tax refunded: chosen at Adjustment click via MUI List (TAXP / TAXR).
 */
const TAX_ROW_FROM_PAPER_CODE: Record<number, string | null> = {
    [TAX_REFUNDABLE_BF_ROW_INDEX]: 'BS-CA-TAXR', // 1.1 Tax (refundable) b/f
    [TAX_PAYABLE_BF_ROW_INDEX]: 'BS-CL-TAXP', // 1.2 Tax payable b/f
    [TAX_PAID_ROW_INDEX]: null, // 1.3 Tax (paid) — user selects TAXP / TAXR
    [TAX_REFUNDED_ROW_INDEX]: null, // 1.4 Tax refunded — user selects TAXP / TAXR
    [TAX_EXPENSES_ROW_INDEX]: 'PL-TAX', // 1.5 Tax expenses during the period
    5: 'PL-TAX', // 1.6 (Over-provided) in prior years
    6: 'PL-TAX', // 1.7 Under-provided in prior years
};

function isTaxPaidOrRefundedLogicalIndex(logicalIndex: number): boolean {
    return logicalIndex === TAX_PAID_ROW_INDEX || logicalIndex === TAX_REFUNDED_ROW_INDEX;
}

export function isTaxWpFsCode(code: string | null | undefined): boolean {
    return TAX_WP_FS_CODES.has(String(code || '').trim().toUpperCase());
}

/** 非 TaxWp → TaxWp：选中 FS code 后默认写入的 TaxWp 行逻辑索引 */
export function resolveTaxWpToPaperDefaultLogicalIndex(
    code: string | null | undefined,
): number | null {
    const normalized = String(code || '').trim().toUpperCase();
    if (!(normalized in TAX_WP_TO_PAPER_DEFAULT_LOGICAL_INDEX)) return null;
    return TAX_WP_TO_PAPER_DEFAULT_LOGICAL_INDEX[normalized];
}

/** 非 TaxWp → Tax 节点：RowSelector 可选逻辑行列表 */
export function resolveTaxWpToPaperSelectableLogicalIndices(
    code: string | null | undefined,
): readonly number[] | null {
    const normalized = String(code || '').trim().toUpperCase();
    if (!(normalized in TAX_WP_TO_PAPER_SELECTABLE_LOGICAL_INDICES)) return null;
    return TAX_WP_TO_PAPER_SELECTABLE_LOGICAL_INDICES[normalized];
}

/**
 * TaxWp 发起 adjustment 时，FsSelectionDrawer 应隐藏的同组 Tax 节点：
 * - Tax (refundable) b/f (BS-CA-TAXR) → 隐藏 BS-CL-TAXP、PL-TAX
 * - Tax payable b/f (BS-CL-TAXP) → 隐藏 BS-CA-TAXR、PL-TAX
 * - Tax expenses / Over-provided / Under-provided (PL-TAX) → 隐藏 BS-CA-TAXR、BS-CL-TAXP
 */
export function getHiddenTaxFsCodesForTaxWpFromPaper(
    fromCode: string | null | undefined,
): ReadonlySet<string> {
    const normalized = String(fromCode || '').trim().toUpperCase();
    if (normalized === 'BS-CA-TAXR') return new Set(['BS-CL-TAXP', 'PL-TAX']);
    if (normalized === 'BS-CL-TAXP') return new Set(['BS-CA-TAXR', 'PL-TAX']);
    if (normalized === 'PL-TAX') return new Set(['BS-CA-TAXR', 'BS-CL-TAXP']);
    return new Set();
}

export function isTaxWpHiddenFsSelectionTarget(
    fromCode: string | null | undefined,
    targetCode: string | null | undefined,
): boolean {
    const hidden = getHiddenTaxFsCodesForTaxWpFromPaper(fromCode);
    if (hidden.size === 0) return false;
    return hidden.has(String(targetCode || '').trim().toUpperCase());
}

function taxRowMatchesToPaperDefault(
    row: {item?: string | null} | null | undefined,
    rowIndex: number,
    logicalIndex: number,
): boolean {
    if (taxLogicalRowIndex(row as CommonRow, rowIndex) === logicalIndex) return true;
    const item = String(row?.item || '').trim().toLowerCase();
    if (logicalIndex === TAX_REFUNDABLE_BF_ROW_INDEX) {
        return item.startsWith('tax (refundable) b/f');
    }
    if (logicalIndex === TAX_PAYABLE_BF_ROW_INDEX) {
        return item.startsWith('tax payable b/f');
    }
    if (logicalIndex === TAX_PAID_ROW_INDEX) {
        return item.startsWith('tax (paid) during');
    }
    if (logicalIndex === TAX_REFUNDED_ROW_INDEX) {
        return item.startsWith('tax refunded during');
    }
    if (logicalIndex === TAX_EXPENSES_ROW_INDEX) {
        return item.startsWith('tax expenses during');
    }
    return false;
}

/**
 * 非 TaxWp 选中 Tax 节点作为 toPaper 时，将 RowSelector 候选收窄为可选行：
 * - BS-CA-TAXR → Tax (refundable) b/f / Tax (paid) / Tax refunded
 * - BS-CL-TAXP → Tax payable b/f / Tax (paid) / Tax refunded
 * - PL-TAX → Tax expenses（单行，可自动绑定）
 */
export function filterTaxWpRowSelectionRows<T extends {
    arrayName: string;
    rowIndex: number;
    rowKey: string;
    row: {item?: string | null} | null;
}>(
    rows: T[],
    selectedFsCode: string,
): T[] {
    const indices = resolveTaxWpToPaperSelectableLogicalIndices(selectedFsCode);
    if (indices == null || indices.length === 0) return rows;

    const matched = rows.filter((r) =>
        r.arrayName === 'commonRow'
        && indices.some((logicalIndex) => taxRowMatchesToPaperDefault(r.row, r.rowIndex, logicalIndex)),
    );
    if (matched.length === 0) return rows;

    const order = new Map(indices.map((logicalIndex, orderIdx) => [logicalIndex, orderIdx]));
    return [...matched].sort((a, b) => {
        const aLogical = taxLogicalRowIndex(a.row as CommonRow, a.rowIndex);
        const bLogical = taxLogicalRowIndex(b.row as CommonRow, b.rowIndex);
        return (order.get(aLogical) ?? 99) - (order.get(bLogical) ?? 99);
    });
}

export type TaxFromPaperTarget = {
    row: CommonRow;
    logicalIndex: number;
    adjustment: number;
    code: string | null;
    isDirect: boolean;
};

export function resolveTaxRowFromPaperCode(logicalIndex: number): string | null {
    return TAX_ROW_FROM_PAPER_CODE[logicalIndex] ?? null;
}

/** 后端回显：识别 TaxWp Tax (refundable) b/f 发起的 BS-CA-TAXR line */
export function isTaxWpRefundableBfAdjustmentLine(
    code: string,
    details: unknown,
): boolean {
    if (!isTaxRefundableBfDtdCode(code)) return false;
    return taxDetailsContainItemPrefix(details, 'tax (refundable) b/f');
}

/** 后端回显：识别 TaxWp Tax payable b/f 发起的 BS-CL-TAXP line */
export function isTaxWpPayableBfAdjustmentLine(
    code: string,
    details: unknown,
): boolean {
    if (!isTaxPayableBfDtdCode(code)) return false;
    return taxDetailsContainItemPrefix(details, 'tax payable b/f');
}

/** TaxWp b/f / paid / refunded special 回显（refundable / payable 节点） */
export function isTaxWpBfSpecialAdjustmentLine(
    code: string,
    details: unknown,
): boolean {
    if (!isTaxBfSpecialDtdCode(code)) return false;
    return taxDetailsContainItemPrefix(details, 'tax (refundable) b/f')
        || taxDetailsContainItemPrefix(details, 'tax payable b/f')
        || taxDetailsContainItemPrefix(details, 'tax (paid) during')
        || taxDetailsContainItemPrefix(details, 'tax refunded during');
}

function taxDetailsContainItemPrefix(details: unknown, prefix: string): boolean {
    const items = Array.isArray(details) ? details : (details ? [details] : []);
    for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        const commonRow = (item as { commonRow?: CommonRow[] }).commonRow;
        if (!Array.isArray(commonRow)) continue;
        if (commonRow.some((row) => {
            const label = String(row?.item || '').trim().toLowerCase();
            return label.startsWith(prefix) || label.includes(prefix);
        })) {
            return true;
        }
    }
    return false;
}

function isSaleMirrorOnlyRow(
    logicalIndex: number,
    rowId: number,
    tablePending: Record<number, number>,
): boolean {
    if (logicalIndex !== TAX_REFUNDABLE_BF_ROW_INDEX && logicalIndex !== TAX_PAYABLE_BF_ROW_INDEX) {
        return false;
    }
    return tablePending[rowId] === undefined;
}

function getRowEffectiveAdjustment(
    row: CommonRow,
    logicalIndex: number,
    pendingAdjustments: Record<number, number>,
): number {
    if (row.id != null && pendingAdjustments[row.id] !== undefined) {
        return resolveTaxAdjustmentSign(logicalIndex, row, pendingAdjustments[row.id]);
    }
    if (!isAdjustmentLocked(row)) {
        return resolveTaxAdjustmentSign(logicalIndex, row, Number(row.adjustment) || 0);
    }
    return 0;
}

/** Collect rows with new (non-locked) adjustments eligible to drive fromPaper. */
export function collectTaxFromPaperTargets(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    tablePending: Record<number, number>,
): TaxFromPaperTarget[] {
    const targets: TaxFromPaperTarget[] = [];

    rows.forEach((row, idx) => {
        const logicalIndex = taxLogicalRowIndex(row, idx);
        if (logicalIndex < 0 || logicalIndex >= TAX_CF_SOURCE_ROW_COUNT) return;

        const rowId = row.id;
        const isDirect = rowId != null && tablePending[rowId] !== undefined;
        const hasPending = rowId != null && Math.abs(pendingAdjustments[rowId] || 0) > 0.001;
        const hasNewRowAdj = !isAdjustmentLocked(row) && Math.abs(Number(row.adjustment) || 0) > 0.001;

        if (!hasPending && !hasNewRowAdj) return;
        if (!isDirect && !hasNewRowAdj && rowId != null && isSaleMirrorOnlyRow(logicalIndex, rowId, tablePending)) {
            return;
        }
        if (!isDirect && !hasNewRowAdj) return;

        const adjustment = getRowEffectiveAdjustment(row, logicalIndex, pendingAdjustments);
        if (Math.abs(adjustment) < 0.001) return;

        targets.push({
            row,
            logicalIndex,
            adjustment,
            code: resolveTaxRowFromPaperCode(logicalIndex),
            isDirect,
        });
    });

    return targets;
}

function pickPrimaryTarget(targets: TaxFromPaperTarget[]): TaxFromPaperTarget | null {
    if (!targets.length) return null;

    const direct = targets.filter((target) => target.isDirect);
    const pool = direct.length > 0 ? direct : targets;
    const sorted = [...pool].sort((a, b) => a.logicalIndex - b.logicalIndex);
    const primary = sorted[0];

    if (!primary.code) return primary;

    const sameCode = pool.filter((target) => target.code === primary.code);
    if (sameCode.length <= 1) return primary;

    const adjustment = sameCode.reduce((sum, target) => sum + target.adjustment, 0);
    return {...primary, adjustment};
}

function buildCategoryLineForCode(
    code: string,
    payloadLine?: FsCategoryLine | null,
): FsCategoryLine {
    const normalized = code.trim().toUpperCase();
    const fsLine = useFsAdjustStore.getState().getLineByCode(normalized);
    const payloadMatches = String(payloadLine?.code || '').trim().toUpperCase() === normalized;

    return {
        ...(fsLine || {}),
        code: normalized,
        scheduleCode: normalized,
        fsId: (payloadMatches ? payloadLine?.fsId : fsLine?.fsId) || fsLine?.fsId || 0,
        entry: (payloadMatches ? payloadLine?.entry : fsLine?.entry) ?? fsLine?.entry,
        name: fsLine?.name || payloadLine?.name,
        scheduleName: fsLine?.scheduleName || payloadLine?.scheduleName,
    } as FsCategoryLine;
}

export interface BuildTaxWpFromPaperParams {
    rows: CommonRow[];
    rowsForPersist: CommonRow[];
    pendingAdjustments: Record<number, number>;
    tablePending: Record<number, number>;
    payloadLine?: FsCategoryLine | null;
    showSnackbar: ShowSnackbarFn;
    statement: 'BS' | 'PL';
    companyId: number;
    annualId: number;
    currency: string;
    totalsFinal: number;
    /** User-selected fromPaper for Tax (paid) / Tax refunded (BS-CL-TAXP / BS-CA-TAXR). */
    selectedFromPaperCode?: string | null;
}

function resolveTaxFromPaperTarget(
    params: BuildTaxWpFromPaperParams,
): TaxFromPaperTarget | null {
    const targets = collectTaxFromPaperTargets(
        params.rows,
        params.pendingAdjustments,
        params.tablePending,
    );
    if (!targets.length) {
        params.showSnackbar('No adjustment row available', 'warning');
        return null;
    }

    const selectedCode = String(params.selectedFromPaperCode || '').trim().toUpperCase();
    const paidRefundedTargets = targets.filter((t) => isTaxPaidOrRefundedLogicalIndex(t.logicalIndex));

    // User confirmed TAXP/TAXR for paid/refunded rows — prefer those targets.
    if (selectedCode === 'BS-CL-TAXP' || selectedCode === 'BS-CA-TAXR') {
        if (paidRefundedTargets.length === 0) {
            params.showSnackbar('No Tax (paid) / Tax refunded adjustment row available', 'warning');
            return null;
        }
        const primary = pickPrimaryTarget(paidRefundedTargets);
        if (!primary) {
            params.showSnackbar('No Tax (paid) / Tax refunded adjustment row available', 'warning');
            return null;
        }
        return {...primary, code: selectedCode};
    }

    const primary = pickPrimaryTarget(targets);
    if (!primary) {
        params.showSnackbar('No adjustment row available', 'warning');
        return null;
    }

    if (isTaxPaidOrRefundedLogicalIndex(primary.logicalIndex)) {
        params.showSnackbar(
            'Please select Tax payable or Tax refundable before adjustment',
            'warning',
        );
        return null;
    }

    if (!primary.code) {
        const itemLabel = String(primary.row.item || '').trim() || 'this row';
        params.showSnackbar(
            `FS code for "${itemLabel}" is pending confirmation (TODO)`,
            'warning',
        );
        return null;
    }
    return primary;
}

/** WP → WP / WP → FS: assemble fromPaper line using per-row FS code mapping. */
export function buildTaxWpFromPaperLine(
    params: BuildTaxWpFromPaperParams,
): Wp2WpResult['line'] | null {
    const primary = resolveTaxFromPaperTarget(params);
    if (!primary?.code) return null;

    const categoryLine = buildCategoryLineForCode(primary.code, params.payloadLine);
    const normalizedCode = primary.code.trim().toUpperCase();
    const useTaxRefundableBfDtd = isTaxRefundableBfDtdCode(normalizedCode);
    const useTaxPayableBfSpecial = isTaxPayableBfDtdCode(normalizedCode);
    const originalEntry = useTaxRefundableBfDtd
        ? getOriginalEntryFromParent(normalizedCode)
        : useTaxPayableBfSpecial
            ? 'Dr'
            : getOriginalEntryFromFsCategory(
                normalizedCode,
                categoryLine.entry === 'Cr' ? 'Cr' : 'Dr',
            );
    const payloadLineForMerge = (String(params.payloadLine?.code || '').trim().toUpperCase() === normalizedCode
        ? params.payloadLine
        : {
            code: normalizedCode,
            fsId: categoryLine.fsId,
            entry: originalEntry,
        }) as FsCategoryLine;

    const wp2wpResult = mergeDataForAdjust({
        categoryLine,
        payloadLine: payloadLineForMerge,
        showSnackbar: params.showSnackbar,
        statement: params.statement,
        companyId: params.companyId,
        annualId: params.annualId,
        currency: params.currency,
        entry: originalEntry,
        totalsFinal: params.totalsFinal,
        amountDifference: primary.adjustment,
        detailsData: {commonRow: params.rowsForPersist},
    });

    const line = wp2wpResult?.line ?? null;
    if (line && useTaxRefundableBfDtd) {
        applyDrMergeLine(line, normalizedCode, originalEntry);
    } else if (line && useTaxPayableBfSpecial) {
        applyTaxPayableBfMergeLine(line);
    }
    return line;
}

